import { useEffect, useRef, useSyncExternalStore } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { tokenStore } from "../api/token";
import { useSession } from "./useSession";

export type TaskEventType =
  | "task.created"
  | "task.updated"
  | "task.moved"
  | "task.deleted"
  | "task-comment.created"
  | "task-comment.updated"
  | "task-comment.deleted"
  | "task-comment.reaction-updated"
  | "notification.created"
  | "notification.read"
  | "task-type.updated";

export interface TaskEvent {
  type: TaskEventType;
  /** Synthetic scope id для tasks-канала: всегда "tasks". */
  launchId: string;
  entityId: string;
  by: { id: string; email: string; name: string | null };
  at: string;
  data?: unknown;
}

// Один общий SSE-канал на воркспейс — мутации задач и типов задач прилетают
// сюда любому клиенту с открытой страницей /tasks. Self-фильтр по
// `event.by.id`: своя мутация уже инвалидировала кэш через onSettled.
export function useTaskEvents(enabled: boolean): void {
  const qc = useQueryClient();
  const session = useSession();
  const currentUserId = session.data?.id ?? null;
  const userIdRef = useRef(currentUserId);
  userIdRef.current = currentUserId;

  const token = useSyncExternalStore(
    tokenStore.subscribe,
    () => tokenStore.get(),
    () => null,
  );

  useEffect(() => {
    if (!enabled) return;
    if (!token && import.meta.env.PROD) return;

    const url = token
      ? `/api/v1/tasks/events?access_token=${encodeURIComponent(token)}`
      : `/api/v1/tasks/events`;

    const source = new EventSource(url, { withCredentials: false });

    const handle = (raw: MessageEvent) => {
      let event: TaskEvent;
      try {
        event = JSON.parse(raw.data);
      } catch {
        return;
      }
      if (
        event.by.id &&
        userIdRef.current &&
        event.by.id === userIdRef.current
      ) {
        return;
      }
      switch (event.type) {
        case "task.created":
        case "task.updated":
        case "task.moved":
        case "task.deleted": {
          qc.invalidateQueries({ queryKey: ["tasks"] });
          break;
        }
        case "task-comment.created":
        case "task-comment.updated":
        case "task-comment.deleted":
        case "task-comment.reaction-updated": {
          qc.invalidateQueries({ queryKey: ["taskComments", event.entityId] });
          break;
        }
        case "notification.created":
        case "notification.read": {
          const data = event.data as { userIds?: string[] } | undefined;
          if (!data?.userIds || data.userIds.includes(userIdRef.current ?? "")) {
            qc.invalidateQueries({ queryKey: ["notifications"] });
          }
          break;
        }
        case "task-type.updated": {
          qc.invalidateQueries({ queryKey: ["taskTypes"] });
          break;
        }
      }
    };

    const types: TaskEventType[] = [
      "task.created",
      "task.updated",
      "task.moved",
      "task.deleted",
      "task-comment.created",
      "task-comment.updated",
      "task-comment.deleted",
      "task-comment.reaction-updated",
      "notification.created",
      "notification.read",
      "task-type.updated",
    ];
    for (const t of types) {
      source.addEventListener(t, handle as EventListener);
    }
    source.addEventListener("message", handle);

    return () => {
      for (const t of types) {
        source.removeEventListener(t, handle as EventListener);
      }
      source.removeEventListener("message", handle);
      source.close();
    };
  }, [enabled, qc, token]);
}
